import {
  POOL_BALL_RADIUS,
  POOL_CENTER_Y,
  POOL_CUE_BALL,
  POOL_FOOT_SPOT_X,
  POOL_HEAD_SPOT_X,
  POOL_POCKETS,
  POOL_POCKET_RADIUS,
  POOL_TABLE_LENGTH,
  POOL_TABLE_WIDTH,
  type PoolPoint,
} from '@awwyeah/shared';
import type { PoolAimPreview } from './physics';
import type { PoolRenderBall } from './playback';

/**
 * Canvas rendering for the pool table. Kept free of React (same split as SketchPad's
 * `sketch/canvas.ts`) so the geometry — `poolLayout`, `toCanvas`, `toTable` — is
 * unit-testable without a DOM, and the component only ever calls `drawTable`.
 *
 * **Theme policy.** The felt and the rails are the *object*, not chrome: a pool table is
 * green with wooden rails in every theme, the same way a photo doesn't recolour in dark
 * mode. Everything *around* the table — headers, trays, buttons, status text, the win
 * screen — is built from semantic tokens in `PoolBoardPage.tsx` and themes normally. So this
 * file holds a handful of fixed object colours and reads exactly one thing from the theme:
 * the current shooter's member colour, which the caller passes in.
 *
 * Rendering is `devicePixelRatio`-correct (`fitCanvasToDpr`, same approach as SketchPad):
 * the backing store is sized in device pixels, the context is pre-scaled, and every draw
 * call below works in CSS pixels.
 */

const FELT = '#1d7350';
const FELT_LIGHT = '#2a8f63';
const FELT_EDGE = '#14563a';
const CUSHION = '#155c3d';
const RAIL = '#6b4226';
const RAIL_LIGHT = '#8a5a34';
const RAIL_DARK = '#3d2414';
const SIGHT = '#efe6d2';
const POCKET = '#0b0806';
const CUE_BALL_COLOR = '#f7f3e8';
const ILLEGAL = '#e0603a';

/** Standard ball colours — 1–7 solid, 9–15 the same seven as stripes, 8 black. */
const BALL_COLORS: Record<number, string> = {
  1: '#e6b422',
  2: '#1f4fa8',
  3: '#c62828',
  4: '#6a3b9e',
  5: '#e8730c',
  6: '#1b7a3d',
  7: '#7b241c',
  8: '#161616',
  9: '#e6b422',
  10: '#1f4fa8',
  11: '#c62828',
  12: '#6a3b9e',
  13: '#e8730c',
  14: '#1b7a3d',
  15: '#7b241c',
};

export function ballColor(n: number): string {
  return n === POOL_CUE_BALL ? CUE_BALL_COLOR : (BALL_COLORS[n] ?? '#888');
}

export function isStripe(n: number): boolean {
  return n >= 9 && n <= 15;
}

/**
 * Where the table sits inside the canvas, and how table units map to CSS pixels.
 *
 * Mobile-first orientation: in a portrait box the table stands up (long axis vertical); in a
 * landscape box it lies flat. Then **`flipped` turns it 180°** so each player sits at their
 * own end, like two people at a real table: the breaker (seat 0) has the head — where the
 * break is played from — at the bottom of their screen, the other player has the foot end
 * at theirs. Purely a display transform: physics, the move log and every stored coordinate
 * stay in table units, and nothing else in the module knows any of this happened —
 * everything goes through `toCanvas`/`toTable`.
 */
export interface PoolLayout {
  /** CSS pixels per table unit. */
  scale: number;
  originX: number;
  originY: number;
  vertical: boolean;
  /** Rotated 180° — the viewer sits at the foot end. */
  flipped: boolean;
  cssWidth: number;
  cssHeight: number;
}

/** Rail width, in table units — the wooden frame drawn outside the playing surface. */
export const RAIL_WIDTH = 0.13;
/** The rubber cushion band drawn between the wood and the felt (inside the rail width). */
const CUSHION_WIDTH = 0.035;

export function poolLayout(cssWidth: number, cssHeight: number, flipped = false): PoolLayout {
  const outerLong = POOL_TABLE_LENGTH + RAIL_WIDTH * 2;
  const outerShort = POOL_TABLE_WIDTH + RAIL_WIDTH * 2;
  const vertical = cssHeight > cssWidth;
  const boxLong = vertical ? cssHeight : cssWidth;
  const boxShort = vertical ? cssWidth : cssHeight;
  const scale = Math.min(boxLong / outerLong, boxShort / outerShort);

  const drawnLong = outerLong * scale;
  const drawnShort = outerShort * scale;
  const padLong = (boxLong - drawnLong) / 2 + RAIL_WIDTH * scale;
  const padShort = (boxShort - drawnShort) / 2 + RAIL_WIDTH * scale;

  return {
    scale,
    originX: vertical ? padShort : padLong,
    originY: vertical ? padLong : padShort,
    vertical,
    flipped,
    cssWidth,
    cssHeight,
  };
}

/** Table coordinates → canvas CSS pixels. */
export function toCanvas(layout: PoolLayout, p: PoolPoint): { cx: number; cy: number } {
  // Rotating 180° is the same as mirroring both table axes.
  const x = layout.flipped ? POOL_TABLE_LENGTH - p.x : p.x;
  const y = layout.flipped ? POOL_TABLE_WIDTH - p.y : p.y;
  if (layout.vertical) {
    return {
      cx: layout.originX + y * layout.scale,
      cy: layout.originY + (POOL_TABLE_LENGTH - x) * layout.scale,
    };
  }
  return { cx: layout.originX + x * layout.scale, cy: layout.originY + y * layout.scale };
}

/** Canvas CSS pixels → table coordinates. Exact inverse of `toCanvas`. */
export function toTable(layout: PoolLayout, cx: number, cy: number): PoolPoint {
  let x: number;
  let y: number;
  if (layout.vertical) {
    x = POOL_TABLE_LENGTH - (cy - layout.originY) / layout.scale;
    y = (cx - layout.originX) / layout.scale;
  } else {
    x = (cx - layout.originX) / layout.scale;
    y = (cy - layout.originY) / layout.scale;
  }
  return layout.flipped ? { x: POOL_TABLE_LENGTH - x, y: POOL_TABLE_WIDTH - y } : { x, y };
}

/** Sizes the backing store for `devicePixelRatio` and returns a context pre-scaled so every
 * draw call works in CSS pixels. Same helper shape as SketchPad's — feature modules may not
 * import each other's internals, so it's copied, not imported (see `useHouseholdRoster`). */
export function fitCanvasToDpr(
  canvas: HTMLCanvasElement,
  cssWidth: number,
  cssHeight: number,
): CanvasRenderingContext2D | null {
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio ?? 1) : 1;
  const targetWidth = Math.max(1, Math.round(cssWidth * dpr));
  const targetHeight = Math.max(1, Math.round(cssHeight * dpr));
  if (canvas.width !== targetWidth) canvas.width = targetWidth;
  if (canvas.height !== targetHeight) canvas.height = targetHeight;
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${cssHeight}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** The felt's on-screen rectangle (orientation-independent). */
function feltRect(layout: PoolLayout): {
  left: number;
  top: number;
  width: number;
  height: number;
} {
  const a = toCanvas(layout, { x: 0, y: 0 });
  const b = toCanvas(layout, { x: POOL_TABLE_LENGTH, y: POOL_TABLE_WIDTH });
  return {
    left: Math.min(a.cx, b.cx),
    top: Math.min(a.cy, b.cy),
    width: Math.abs(b.cx - a.cx),
    height: Math.abs(b.cy - a.cy),
  };
}

/** Rail sights ("diamonds"): eighths of the long rails and quarters of the short ones,
 * skipping the pockets — real tables have them, and they're a quiet aiming reference. */
function sightPoints(): PoolPoint[] {
  const out: PoolPoint[] = [];
  const off = RAIL_WIDTH * 0.55;
  for (let k = 1; k < 8; k += 1) {
    if (k === 4) continue;
    const x = (POOL_TABLE_LENGTH * k) / 8;
    out.push({ x, y: -off }, { x, y: POOL_TABLE_WIDTH + off });
  }
  for (let k = 1; k < 4; k += 1) {
    const y = (POOL_TABLE_WIDTH * k) / 4;
    out.push({ x: -off, y }, { x: POOL_TABLE_LENGTH + off, y });
  }
  return out;
}

function drawSurface(ctx: CanvasRenderingContext2D, layout: PoolLayout): void {
  const { left, top, width, height } = feltRect(layout);
  const s = layout.scale;
  const rail = RAIL_WIDTH * s;
  const cushion = CUSHION_WIDTH * s;

  // Drop shadow under the whole table so it sits *on* the page.
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = Math.max(4, rail * 0.6);
  ctx.shadowOffsetY = Math.max(2, rail * 0.2);
  ctx.fillStyle = RAIL_DARK;
  roundRect(ctx, left - rail, top - rail, width + rail * 2, height + rail * 2, rail * 0.8);
  ctx.fill();
  ctx.restore();

  // Wooden rails: a warm diagonal gradient, a few faint grain lines, a bevelled edge.
  const wood = ctx.createLinearGradient(
    left - rail,
    top - rail,
    left + width + rail,
    top + height + rail,
  );
  wood.addColorStop(0, RAIL_LIGHT);
  wood.addColorStop(0.45, RAIL);
  wood.addColorStop(1, RAIL_DARK);
  ctx.fillStyle = wood;
  roundRect(ctx, left - rail, top - rail, width + rail * 2, height + rail * 2, rail * 0.8);
  ctx.fill();

  ctx.save();
  roundRect(ctx, left - rail, top - rail, width + rail * 2, height + rail * 2, rail * 0.8);
  ctx.clip();
  ctx.strokeStyle = 'rgba(255,230,190,0.07)';
  ctx.lineWidth = 1;
  const long = layout.vertical ? height : width;
  for (let i = 0; i < 9; i += 1) {
    const f = (i + 0.5) / 9;
    ctx.beginPath();
    if (layout.vertical) {
      const x = left - rail + (width + rail * 2) * f;
      ctx.moveTo(x, top - rail);
      ctx.bezierCurveTo(
        x + rail * 0.3,
        top + long * 0.3,
        x - rail * 0.3,
        top + long * 0.7,
        x,
        top + height + rail,
      );
    } else {
      const y = top - rail + (height + rail * 2) * f;
      ctx.moveTo(left - rail, y);
      ctx.bezierCurveTo(
        left + long * 0.3,
        y + rail * 0.3,
        left + long * 0.7,
        y - rail * 0.3,
        left + width + rail,
        y,
      );
    }
    ctx.stroke();
  }
  ctx.restore();

  ctx.strokeStyle = 'rgba(0,0,0,0.45)';
  ctx.lineWidth = Math.max(1, rail * 0.12);
  roundRect(ctx, left - rail, top - rail, width + rail * 2, height + rail * 2, rail * 0.8);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,235,200,0.18)';
  ctx.lineWidth = 1;
  roundRect(
    ctx,
    left - rail * 0.88,
    top - rail * 0.88,
    width + rail * 1.76,
    height + rail * 1.76,
    rail * 0.7,
  );
  ctx.stroke();

  // Sights.
  ctx.fillStyle = SIGHT;
  for (const point of sightPoints()) {
    const { cx, cy } = toCanvas(layout, point);
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(1.2, rail * 0.1), 0, Math.PI * 2);
    ctx.fill();
  }

  // Cushion rubber: a darker green band just inside the wood.
  ctx.fillStyle = CUSHION;
  ctx.fillRect(left - cushion, top - cushion, width + cushion * 2, height + cushion * 2);

  // Felt: a lamp-lit radial gradient, brightest in the middle.
  const cx = left + width / 2;
  const cy = top + height / 2;
  const felt = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.hypot(width, height) * 0.6);
  felt.addColorStop(0, FELT_LIGHT);
  felt.addColorStop(0.6, FELT);
  felt.addColorStop(1, FELT_EDGE);
  ctx.fillStyle = felt;
  ctx.fillRect(left, top, width, height);
  // Inner shadow off the cushions.
  ctx.strokeStyle = 'rgba(0,0,0,0.22)';
  ctx.lineWidth = Math.max(1, s * 0.025);
  ctx.strokeRect(
    left + ctx.lineWidth / 2,
    top + ctx.lineWidth / 2,
    width - ctx.lineWidth,
    height - ctx.lineWidth,
  );

  // Head string and foot spot, faint — chalk marks, not UI.
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 1;
  const h1 = toCanvas(layout, { x: POOL_HEAD_SPOT_X, y: 0 });
  const h2 = toCanvas(layout, { x: POOL_HEAD_SPOT_X, y: POOL_TABLE_WIDTH });
  ctx.beginPath();
  ctx.moveTo(h1.cx, h1.cy);
  ctx.lineTo(h2.cx, h2.cy);
  ctx.stroke();
  const foot = toCanvas(layout, { x: POOL_FOOT_SPOT_X, y: POOL_CENTER_Y });
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.beginPath();
  ctx.arc(foot.cx, foot.cy, Math.max(1, s * 0.012), 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Pockets: a dark hole with depth, and a leather rim.
  for (const pocket of POOL_POCKETS) {
    const { cx: px, cy: py } = toCanvas(layout, pocket);
    const pr = POOL_POCKET_RADIUS * s;
    const hole = ctx.createRadialGradient(px, py, 0, px, py, pr);
    hole.addColorStop(0, '#000');
    hole.addColorStop(0.7, POCKET);
    hole.addColorStop(1, '#2a1c13');
    ctx.fillStyle = hole;
    ctx.beginPath();
    ctx.arc(px, py, pr, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.6)';
    ctx.lineWidth = Math.max(1, pr * 0.14);
    ctx.stroke();
  }
}

function drawBallShadows(
  ctx: CanvasRenderingContext2D,
  layout: PoolLayout,
  balls: readonly PoolRenderBall[],
): void {
  ctx.fillStyle = 'rgba(0,0,0,0.28)';
  for (const ball of balls) {
    if ((ball.sink ?? 0) > 0) continue;
    const { cx, cy } = toCanvas(layout, ball);
    const r = POOL_BALL_RADIUS * layout.scale * ball.scale;
    if (r < 0.4) continue;
    ctx.beginPath();
    ctx.ellipse(cx + r * 0.22, cy + r * 0.3, r * 1.02, r * 0.9, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawBall(ctx: CanvasRenderingContext2D, layout: PoolLayout, ball: PoolRenderBall): void {
  const { cx, cy } = toCanvas(layout, ball);
  const r = POOL_BALL_RADIUS * layout.scale * Math.max(0, ball.scale);
  if (r < 0.4) return;
  const sink = ball.sink ?? 0;

  ctx.save();
  if (sink > 0) ctx.globalAlpha = Math.max(0, 1 - sink * sink);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();

  if (isStripe(ball.n)) {
    ctx.fillStyle = CUE_BALL_COLOR;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    ctx.fillStyle = ballColor(ball.n);
    ctx.fillRect(cx - r, cy - r * 0.5, r * 2, r);
  } else {
    ctx.fillStyle = ballColor(ball.n);
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  }

  // Number disc — drawn whenever it can hold a legible digit.
  if (ball.n !== POOL_CUE_BALL && r >= 5.5) {
    ctx.fillStyle = CUE_BALL_COLOR;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#161616';
    ctx.font = `700 ${Math.max(6, Math.round(r * (ball.n >= 10 ? 0.62 : 0.74)))}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(ball.n), cx, cy + r * 0.05);
  }

  // Shading: a dark rim for roundness, then a specular highlight from an overhead lamp.
  const shade = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.35, r * 0.1, cx, cy, r * 1.05);
  shade.addColorStop(0, 'rgba(255,255,255,0)');
  shade.addColorStop(0.65, 'rgba(0,0,0,0.05)');
  shade.addColorStop(1, 'rgba(0,0,0,0.42)');
  ctx.fillStyle = shade;
  ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  const spec = ctx.createRadialGradient(
    cx - r * 0.35,
    cy - r * 0.4,
    0,
    cx - r * 0.35,
    cy - r * 0.4,
    r * 0.55,
  );
  spec.addColorStop(0, 'rgba(255,255,255,0.85)');
  spec.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = spec;
  ctx.fillRect(cx - r, cy - r, r * 2, r * 2);

  if (sink > 0) {
    ctx.fillStyle = `rgba(0,0,0,${Math.min(0.85, sink)})`;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  }
  ctx.restore();
}

export interface PoolAimOverlay {
  from: PoolPoint;
  angle: number;
  preview: PoolAimPreview;
  /** `0`–`1` — how far the cue is pulled back on the power bar. */
  pull: number;
}

/** The cue stick, drawn lying behind the cue ball along the aim, slid back by `pull`. */
function drawCueStick(
  ctx: CanvasRenderingContext2D,
  layout: PoolLayout,
  from: PoolPoint,
  angle: number,
  pull: number,
): void {
  const s = layout.scale;
  const start = toCanvas(layout, from);
  // Screen direction of the aim (toCanvas is a rotation, so map a unit step).
  const ahead = toCanvas(layout, { x: from.x + Math.cos(angle), y: from.y + Math.sin(angle) });
  let ux = ahead.cx - start.cx;
  let uy = ahead.cy - start.cy;
  const len = Math.hypot(ux, uy) || 1;
  ux /= len;
  uy /= len;
  const nx = -uy;
  const ny = ux;

  const gap = POOL_BALL_RADIUS * s * 1.25 + pull * 0.42 * s;
  const length = 1.45 * s;
  const tipW = 0.012 * s;
  const buttW = 0.026 * s;
  const at = (d: number): { x: number; y: number } => ({
    x: start.cx - ux * d,
    y: start.cy - uy * d,
  });
  const width = (d: number): number => tipW + (buttW - tipW) * ((d - gap) / length);

  const segment = (d0: number, d1: number, fill: string | CanvasGradient): void => {
    const a = at(d0);
    const b = at(d1);
    const w0 = width(d0);
    const w1 = width(d1);
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(a.x + nx * w0, a.y + ny * w0);
    ctx.lineTo(b.x + nx * w1, b.y + ny * w1);
    ctx.lineTo(b.x - nx * w1, b.y - ny * w1);
    ctx.lineTo(a.x - nx * w0, a.y - ny * w0);
    ctx.closePath();
    ctx.fill();
  };

  ctx.save();
  // Shadow on the felt.
  ctx.save();
  ctx.translate(POOL_BALL_RADIUS * s * 0.35, POOL_BALL_RADIUS * s * 0.5);
  segment(gap, gap + length, 'rgba(0,0,0,0.25)');
  ctx.restore();

  const shaftEnd = gap + length * 0.62;
  const maple = ctx.createLinearGradient(
    start.cx + nx * buttW,
    start.cy + ny * buttW,
    start.cx - nx * buttW,
    start.cy - ny * buttW,
  );
  maple.addColorStop(0, '#f6e3b8');
  maple.addColorStop(0.5, '#e2c48c');
  maple.addColorStop(1, '#b89462');
  segment(gap, gap + 0.018 * s, '#3f6fb0'); // chalked tip
  segment(gap + 0.018 * s, gap + 0.05 * s, '#f4f1ea'); // ferrule
  segment(gap + 0.05 * s, shaftEnd, maple);
  segment(shaftEnd, shaftEnd + 0.015 * s, '#e9e1cf'); // joint ring
  segment(shaftEnd + 0.015 * s, gap + length * 0.9, '#5a2f1c');
  segment(gap + length * 0.9, gap + length, '#1e1410');
  ctx.restore();
}

function drawAim(
  ctx: CanvasRenderingContext2D,
  layout: PoolLayout,
  aim: PoolAimOverlay,
  turnColor: string,
): void {
  const s = layout.scale;
  const r = POOL_BALL_RADIUS * s;
  const from = toCanvas(layout, aim.from);
  const { preview } = aim;
  const ghost = toCanvas(layout, preview.ghost);

  // Everything but the stick stays on the felt — a guideline over the rails reads as the
  // ball leaving the table.
  const felt = feltRect(layout);
  ctx.save();
  ctx.beginPath();
  ctx.rect(felt.left, felt.top, felt.width, felt.height);
  ctx.clip();

  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgba(255,255,255,0.82)';
  ctx.lineWidth = Math.max(1.5, s * 0.012);
  ctx.beginPath();
  // Start at the cue ball's edge, end at the ghost ball's edge.
  const dist = Math.hypot(ghost.cx - from.cx, ghost.cy - from.cy);
  if (dist > r * 2) {
    const ux = (ghost.cx - from.cx) / dist;
    const uy = (ghost.cy - from.cy) / dist;
    ctx.moveTo(from.cx + ux * r, from.cy + uy * r);
    ctx.lineTo(ghost.cx - ux * r, ghost.cy - uy * r);
    ctx.stroke();
  }

  // Ghost ball.
  ctx.lineWidth = Math.max(1.5, s * 0.01);
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.beginPath();
  ctx.arc(ghost.cx, ghost.cy, r, 0, Math.PI * 2);
  ctx.stroke();

  const line = (vec: PoolPoint, origin: PoolPoint, color: string): void => {
    const a = toCanvas(layout, origin);
    const b = toCanvas(layout, { x: origin.x + vec.x, y: origin.y + vec.y });
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.moveTo(a.cx, a.cy);
    ctx.lineTo(b.cx, b.cy);
    ctx.stroke();
  };
  ctx.lineWidth = Math.max(1.5, s * 0.012);
  if (preview.objectLine && preview.hitBall !== null) {
    const target = { x: preview.ghost.x, y: preview.ghost.y };
    // From the object ball's centre: ghost + 2r along the line of centres.
    const len = Math.hypot(preview.objectLine.x, preview.objectLine.y);
    if (len > 1e-4) {
      const ux = preview.objectLine.x / len;
      const uy = preview.objectLine.y / len;
      const centre = {
        x: target.x + ux * POOL_BALL_RADIUS * 2,
        y: target.y + uy * POOL_BALL_RADIUS * 2,
      };
      line(preview.objectLine, centre, 'rgba(255,255,255,0.85)');
    }
  }
  if (preview.cueLine) {
    ctx.setLineDash([Math.max(2, s * 0.02), Math.max(2, s * 0.02)]);
    line(preview.cueLine, preview.ghost, 'rgba(255,255,255,0.55)');
    ctx.setLineDash([]);
  }
  ctx.restore();

  drawCueStick(ctx, layout, aim.from, aim.angle, aim.pull);

  // Power ring around the cue ball, in the shooter's member colour, while pulling.
  if (aim.pull > 0) {
    ctx.save();
    ctx.strokeStyle = turnColor;
    ctx.lineWidth = Math.max(2, r * 0.4);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(from.cx, from.cy, r * 1.75, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * aim.pull);
    ctx.stroke();
    ctx.restore();
  }
}

export interface PoolSceneOptions {
  balls: readonly PoolRenderBall[];
  /** Member colour of whoever is shooting — the one theme-derived value on the table. */
  turnColor: string;
  /** Draw a soft ring around the cue ball to say "it's this person's shot". */
  highlightCue: boolean;
  aim?: PoolAimOverlay;
  /** Ball-in-hand: where the cue ball will go, and — when the finger is somewhere it can't
   * go — the finger's own spot, drawn as a red ring. */
  placement?: { at: PoolPoint; finger: PoolPoint | null; legal: boolean };
}

/** One full repaint. Cheap enough (a few gradients, six pockets and at most sixteen balls)
 * that incremental drawing would be pure complexity — same call every frame of an animation. */
export function drawTable(
  ctx: CanvasRenderingContext2D,
  layout: PoolLayout,
  scene: PoolSceneOptions,
): void {
  ctx.clearRect(0, 0, layout.cssWidth, layout.cssHeight);
  drawSurface(ctx, layout);
  const r = POOL_BALL_RADIUS * layout.scale;

  if (scene.highlightCue) {
    const cue = scene.balls.find((b) => b.n === POOL_CUE_BALL);
    if (cue) {
      const { cx, cy } = toCanvas(layout, cue);
      ctx.save();
      ctx.strokeStyle = scene.turnColor;
      ctx.globalAlpha = 0.9;
      ctx.lineWidth = Math.max(1.5, r * 0.3);
      ctx.beginPath();
      ctx.arc(cx, cy, r * 1.55, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  // Sinking balls first — they're *in* the pocket, under everything on the felt.
  const sinking = scene.balls.filter((b) => (b.sink ?? 0) > 0);
  const rolling = scene.balls.filter((b) => (b.sink ?? 0) <= 0);
  for (const ball of sinking) drawBall(ctx, layout, ball);
  drawBallShadows(ctx, layout, rolling);
  for (const ball of rolling) drawBall(ctx, layout, ball);
  // Placement marks go on top: the red ring is under your finger, often over a ball.
  if (scene.placement) {
    const { cx, cy } = toCanvas(layout, scene.placement.at);
    ctx.save();
    ctx.strokeStyle = 'rgba(160,240,190,0.95)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.9, 0, Math.PI * 2);
    ctx.stroke();
    if (!scene.placement.legal && scene.placement.finger) {
      const f = toCanvas(layout, scene.placement.finger);
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = ILLEGAL;
      ctx.beginPath();
      ctx.arc(f.cx, f.cy, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(f.cx - r * 0.6, f.cy - r * 0.6);
      ctx.lineTo(f.cx + r * 0.6, f.cy + r * 0.6);
      ctx.moveTo(f.cx + r * 0.6, f.cy - r * 0.6);
      ctx.lineTo(f.cx - r * 0.6, f.cy + r * 0.6);
      ctx.stroke();
    }
    ctx.restore();
  }

  // The guideline and stick go over the balls — you need to see them most where it's busy.
  if (scene.aim) drawAim(ctx, layout, scene.aim, scene.turnColor);
}
